import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'Supporting Your Local Church';
const DESCRIPTION =
    'Why supporting your local church matters — how giving sustains ministry, fuels growth, and puts faith into action, in finances and in service.';

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/3-community.jpg',
            datePublished: '2026-07-14',
            dateModified: '2026-07-14',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: { '@id': 'https://devotionfund.com/resources/supporting-your-local-church', '@type': 'WebPage' },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'Why should I give to my local church instead of only other ministries?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Your local church is the community that directly disciples you, your family, and your neighbors, and it depends on member giving to keep functioning. Supporting outside ministries is valuable too, but your local church's health directly shapes the spiritual life of your own community.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'What does my giving to church actually pay for?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Regular church giving covers operational costs like building maintenance and utilities, staff salaries, and ministry programs — plus outreach initiatives, youth ministries, and community services that help the church grow and serve beyond its own walls.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Can I support my church without giving money?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Yes — volunteering your time and talents for ministries, fundraising events, or administrative tasks is a real and valuable form of support. A healthy church runs on both financial giving and the service of its members; neither replaces the other.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Is tithing required to be a good church member?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Giving in the Bible is framed as a cheerful, faith-driven response to God's provision (2 Corinthians 9:7) rather than a rigid membership requirement. What matters most is regular, intentional giving within your means, alongside showing up to serve the congregation in other ways.",
                    },
                },
            ],
        },
    ],
};

export default function SupportingLocalChurch() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/supporting-your-local-church' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-supporting-local-church-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/3-community.jpg" alt="Supporting Your Local Church" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">The Cornerstone of Community: Why Supporting Your Local Church Matters</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>The church is more than just a building; it's a vibrant community of believers united by their faith in Christ. But like any community, it requires resources to thrive and fulfill its mission. This is where church giving comes in – a cornerstone of financial support that allows churches to function and flourish.</p>
                    <p>Here's why supporting your local church financially is an act of faith and fellowship:</p>
                    <ul>
                        <li><strong>Sustaining Ministry:</strong> Your contributions help cover operational costs like building maintenance, utilities, staff salaries, and ministry programs. Without this support, core functions that nurture the faith and well-being of the congregation would be at risk.</li>
                        <li><strong>Investing in Growth:</strong> Church giving fuels initiatives like outreach programs, youth ministries, and community services. These efforts allow churches to share their message, attract new members, and make a positive impact on the lives of others.</li>
                        <li><strong>Living the Gospel:</strong> The Bible teaches the importance of generosity (Proverbs 3:9, Malachi 3:10). Giving to your church allows you to put your faith into action and demonstrate love for your fellow congregants. It's a tangible way to participate in the collective act of worship and service.</li>
                        <li><strong>Shared Responsibility:</strong> A healthy church relies on the financial contributions of its members. Regular giving fosters a sense of shared responsibility, ensuring the church has the resources to continue its work and support its members.</li>
                        <li><strong>Building a Legacy:</strong> By supporting your church, you're contributing to a legacy of faith that will benefit future generations. Your generosity allows the church to continue its mission of spreading the Gospel and serving the community.</li>
                    </ul>
                    <p><strong>Giving Beyond the Offering Plate:</strong></p>
                    <p>While financial contributions are vital, remember that giving comes in many forms. Consider volunteering your time and talents to church ministries, fundraising events, or administrative tasks. This kind of support is equally valuable and allows everyone to contribute according to their means.</p>
                    <p>Ultimately, supporting your local church is an investment in your faith, your community, and the future of the church itself. By giving generously, you become an active participant in the life of your church, fostering a place of worship, growth, and service for all.</p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/supporting-your-local-church" />
                </div>
            </div>
            <Footer />
        </div>
    );
}


